import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import mesApi, {
  LabosDeviceKey,
  LabosMeasurementListItem,
  LabosTrialLink,
  LabosTrialOption,
} from "../../../Api/mes.api";
import LabosShell from "./components/LabosShell";
import {
  DEVICE_LABEL,
  DEVICE_TABS,
  formatLabosDate,
  formatLabosDateTime,
  validityBadge,
} from "./labosUtils";

const LabosMeasurements = () => {
  const [device, setDevice] = useState<LabosDeviceKey>("farinograph");
  const [days, setDays] = useState(90);
  const [showInvalid, setShowInvalid] = useState(true);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<LabosMeasurementListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [trials, setTrials] = useState<LabosTrialOption[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [linkTrialId, setLinkTrialId] = useState<number | "">("");
  const [linkNote, setLinkNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mesApi.getLabosMeasurements(device, {
        days,
        showInvalid,
        search: search.trim() || undefined,
      });
      setItems(data);
    } catch (err: any) {
      toast.error(err.message || "Greška pri učitavanju mjerenja");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [device, days, showInvalid, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    mesApi.getLabosTrialsSelect().then(setTrials).catch(() => {});
  }, []);

  const openDetail = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetailLoading(true);
    setLinkTrialId("");
    setLinkNote("");
    try {
      const d = await mesApi.getLabosMeasurementDetail(device, id);
      setDetail(d);
    } catch (err: any) {
      toast.error(err.message);
      setExpandedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleLink = async (measurementId: number) => {
    if (!linkTrialId) {
      toast.error("Odaberite probu");
      return;
    }
    try {
      const d = await mesApi.linkLabosMeasurement(device, measurementId, Number(linkTrialId), undefined, linkNote || undefined);
      setDetail(d);
      toast.success("Veza kreirana");
      load();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleUnlink = async (linkId: number, measurementId: number) => {
    try {
      await mesApi.unlinkLabosTrialLink(linkId);
      toast.success("Veza uklonjena");
      const d = await mesApi.getLabosMeasurementDetail(device, measurementId);
      setDetail(d);
      load();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const linkedTrialIds = new Set((detail?.trial_links || []).map((l: LabosTrialLink) => l.trial_id));
  const availableTrials = trials.filter((t) => !linkedTrialIds.has(t.trial_id));

  const extraMeta = (row: LabosMeasurementListItem) => {
    if (device === "farinograph" && row.sample_weight != null) {
      return `Težina uzorka: ${row.sample_weight} g`;
    }
    if (device === "extensograph" && row.water_absorption != null) {
      return `Apsorpcija vode: ${row.water_absorption} %`;
    }
    if (device === "amylograph") {
      const parts: string[] = [];
      if (row.material_moisture != null) parts.push(`Vlaga: ${row.material_moisture} %`);
      if (row.sample_weight != null) parts.push(`Težina: ${row.sample_weight} g`);
      return parts.join(" · ");
    }
    return null;
  };

  return (
    <LabosShell title="MES - Labos mjerenja">
      <ToastContainer position="top-center" autoClose={2500} />

      <div className="mes-section">
        <h3 style={{ margin: 0, border: "none", padding: 0 }}>Labos mjerenja</h3>
        <p className="mes-hint" style={{ marginTop: 8, marginBottom: 0 }}>
          Pregled mjerenja s uređaja (pipeline). Ručno povežite mjerenje s probom.
        </p>
      </div>

      <div className="mes-section">
        <div className="kpi-tabs fq-analysis-tabs" style={{ marginBottom: 12 }}>
          {DEVICE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`kpi-tab ${device === t.key ? "active" : ""}`}
              onClick={() => {
                setDevice(t.key);
                setExpandedId(null);
                setDetail(null);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="control-group">
            <label>Razdoblje</label>
            <select className="select-classic" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[30, 60, 90, 180, 365].map((d) => (
                <option key={d} value={d}>
                  Zadnjih {d} dana
                </option>
              ))}
            </select>
          </div>
          <div className="control-group">
            <label>
              <input type="checkbox" checked={showInvalid} onChange={(e) => setShowInvalid(e.target.checked)} /> Prikaži i
              nevalidna
            </label>
          </div>
          <div className="control-group" style={{ flex: 1, minWidth: 200 }}>
            <label>Pretraži uzorak</label>
            <input
              className="input-classic"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="npr. T550"
            />
          </div>
          <button type="button" className="btn-secondary btn-sm" onClick={load} disabled={loading}>
            Osvježi
          </button>
        </div>
      </div>

      <div className="mes-section">
        <p className="mes-hint">{loading ? "Učitavanje…" : `${items.length} mjerenja — ${DEVICE_LABEL[device]}`}</p>

        {!loading && items.length === 0 && <p className="mes-hint">Nema mjerenja za odabrane filtere.</p>}

        <div className="labos-measurement-list">
          {items.map((row) => {
            const open = expandedId === row.id;
            const extra = extraMeta(row);
            return (
              <div key={row.id} className={`labos-measurement-card ${open ? "open" : ""}`}>
                <button type="button" className="labos-measurement-head" onClick={() => openDetail(row.id)}>
                  <span>{validityBadge(row.is_valid)}</span>
                  <span>{formatLabosDateTime(row.measured_at)}</span>
                  <span style={{ fontWeight: 600 }}>{row.sample_name || "—"}</span>
                  <span className="mes-hint">({row.method || "—"})</span>
                  {row.link_count > 0 && <span className="status-pill status-confirmed">{row.link_count} veza</span>}
                </button>

                {open && (
                  <div className="labos-measurement-body">
                    {detailLoading && <p>Učitavanje detalja…</p>}
                    {!detailLoading && detail && (
                      <>
                        <div className="form-grid-2" style={{ marginBottom: 12 }}>
                          <div>
                            <strong>Uzorak:</strong> {row.sample_name || "—"}
                            <br />
                            <strong>Metoda:</strong> {row.method || "—"}
                          </div>
                          <div>
                            <strong>Operator:</strong> {row.operator || "—"}
                            <br />
                            <strong>Mješavina:</strong> {row.is_blend ? "Da" : "Ne"}
                          </div>
                        </div>
                        {extra && <p className="mes-hint">{extra}</p>}
                        {row.remarks_raw && !row.is_blend && <p className="mes-hint">Remarks: {row.remarks_raw}</p>}

                        {detail.blend_components?.length > 0 && (
                          <>
                            <h4>Komponente mješavine</h4>
                            <div className="mes-table-wrap">
                              <table className="mes-table">
                              <thead>
                                <tr>
                                  <th>Lot</th>
                                  <th>Dobavljač / sirovina</th>
                                  <th>Tip</th>
                                  <th>Udio %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.blend_components.map((b: any, i: number) => (
                                  <tr key={i}>
                                    <td>{b.lot_number || "—"}</td>
                                    <td>{b.supplier || "—"}</td>
                                    <td>{b.flour_type || "—"}</td>
                                    <td>{b.proportion_pct}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                          </>
                        )}

                        {detail.results?.length > 0 && (
                          <>
                            <h4>Rezultati</h4>
                            <div style={{ overflowX: "auto" }}>
                              <table className="mes-table mes-table-compact">
                                <thead>
                                  <tr>
                                    {Object.keys(detail.results[0]).map((k) => (
                                      <th key={k}>{k}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.results.map((r: any, i: number) => (
                                    <tr key={i}>
                                      {Object.values(r).map((v: any, j) => (
                                        <td key={j}>{v != null ? String(v) : "—"}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}

                        <hr style={{ margin: "1rem 0", opacity: 0.2 }} />
                        <h4>Veza s probom</h4>
                        {(detail.trial_links || []).map((lnk: LabosTrialLink) => (
                          <div key={lnk.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                            <span>
                              {lnk.trial_code} — {formatLabosDate(lnk.trial_date)} — {lnk.product_name || "—"}
                            </span>
                            <button
                              type="button"
                              className="btn-sm btn-danger"
                              onClick={() => handleUnlink(lnk.id, row.id)}
                            >
                              Ukloni
                            </button>
                          </div>
                        ))}

                        {availableTrials.length > 0 ? (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                            <div className="control-group" style={{ minWidth: 280 }}>
                              <label>Proba</label>
                              <select
                                className="select-classic"
                                value={linkTrialId}
                                onChange={(e) =>
                                  setLinkTrialId(e.target.value ? Number(e.target.value) : "")
                                }
                              >
                                <option value="">— odaberi —</option>
                                {availableTrials.map((t) => (
                                  <option key={t.trial_id} value={t.trial_id}>
                                    {t.trial_code} — {formatLabosDate(t.trial_date)} — {t.product_name || "—"}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="control-group" style={{ minWidth: 200 }}>
                              <label>Napomena</label>
                              <input
                                className="input-classic"
                                value={linkNote}
                                onChange={(e) => setLinkNote(e.target.value)}
                                placeholder="opcionalno"
                              />
                            </div>
                            <button type="button" className="btn-primary btn-sm" onClick={() => handleLink(row.id)}>
                              Poveži
                            </button>
                          </div>
                        ) : (
                          <p className="mes-hint">Sve dostupne probe su već povezane.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </LabosShell>
  );
};

export default LabosMeasurements;
